/**
 * WEB-1012 — экран загрузки берёт содержимое из САМОЙ воронки.
 *
 * Зачем: у образцов рынка загрузка не пустая пауза, а часть воронки — она
 * называет реальные шаги («Считаем ваш индекс», «Подбираем программу») и
 * подкрепляет ожидание отзывами. У нас стадии по умолчанию были зашитым
 * английским текстом, одинаковым для любой воронки.
 *
 * Что здесь считается «секцией воронки»: ЭКРАН и его `name`. Это не догадка —
 * то же имя уже показывается покупателю подписью раздела под прогрессом
 * (QuizScreenWizard, `data-testid="quiz-section-label"`, включается полем пака
 * `chrome.progressLabel`). То есть источник уже покупательский, а не служебный.
 *
 * Модуль чистый (без React и DOM): его гоняет и юнит, и визард.
 */
import type { EditorScreen, LoaderStep } from '@/store/editorSlice';
import { richFieldToPlainText, type RichField } from '@/types/rich-text';

/**
 * Авто-имя экрана, которое покупателю показывать нельзя (класс дефекта
 * WEB-1011: на живой экран вылезла служебная запись «done»).
 *
 * Список намеренно ASCII-только и узкий: единственное авто-имя, которое РОЖДАЕТ
 * наш код, — `Screen N` из editorSlice.addScreen, и оно английское при любом
 * языке воронки. Кириллических вариантов сюда добавлять нечего — их никто не
 * генерирует, а строковые литералы плеера обязаны жить в словаре (гард WEB-967).
 * Голое число — вторая форма «имени по умолчанию», встречается в импортах.
 */
const AUTO_SECTION_NAME = /^(screen|step|slide)?[\s\-_]*\d*$/i;

/** Блок, из которого собирается карусель отзывов. Новых сущностей не заводим. */
const REVIEW_BLOCK_TYPE = 'testimonials';

/** Сколько цитат имеет смысл держать в карусели — дальше их никто не досмотрит. */
const REVIEW_QUOTES_LIMIT = 5;

interface ReviewItem {
  text?: RichField;
}

function isUsableSectionName(name: unknown): name is string {
  if (typeof name !== 'string') return false;
  const trimmed = name.trim();
  return trimmed !== '' && !AUTO_SECTION_NAME.test(trimmed);
}

/**
 * Секции, которые покупатель УЖЕ прошёл к моменту загрузки: экраны до того, на
 * котором стоит блок лоадера. Экран самого лоадера и всё после него в стадии не
 * идут — это ещё не пройденный путь, обещать его как сделанное нечестно.
 */
export function funnelSectionNames(
  screens: readonly EditorScreen[],
  loaderBlockId?: string,
): string[] {
  const loaderIndex = loaderBlockId
    ? screens.findIndex((s) => (s.blocks ?? []).some((b) => b.id === loaderBlockId))
    : -1;
  const walked = loaderIndex >= 0 ? screens.slice(0, loaderIndex) : screens;
  const names: string[] = [];
  walked.forEach((s) => {
    if (!isUsableSectionName(s.name)) return;
    const name = s.name.trim();
    if (!names.includes(name)) names.push(name);
  });
  return names;
}

/**
 * Стадии лоадера из секций воронки.
 *
 * `fallback` — сегодняшний запасной список (локализованный, publicQuizStrings).
 * Он задаёт и поведение «нечего взять — оставь как было», и ТАЙМИНГ: длительности
 * берутся из него поимённо, поэтому экран загрузки не может стать длиннее, чем
 * был (приёмка WEB-1012 п.5). Секций больше, чем стадий в запасном списке —
 * берём ближайшие к лоадеру: они свежее в памяти покупателя.
 */
export function funnelSectionStages(
  screens: readonly EditorScreen[],
  loaderBlockId: string | undefined,
  fallback: readonly LoaderStep[],
): LoaderStep[] {
  const names = funnelSectionNames(screens, loaderBlockId);
  if (names.length === 0 || fallback.length === 0) return fallback as LoaderStep[];
  const used = names.slice(Math.max(names.length - fallback.length, 0));
  return used.map((label, index) => ({ label, durationMs: fallback[index].durationMs }));
}

/**
 * Цитаты для карусели — из блоков отзывов ЭТОЙ ЖЕ воронки. Своей сущности
 * «отзывы лоадера» не заводим: она разошлась бы с тем, что покупатель читает
 * на пейволле, и её пришлось бы наполнять второй раз.
 */
export function funnelReviewQuotes(
  screens: readonly EditorScreen[],
  limit = REVIEW_QUOTES_LIMIT,
): string[] {
  const quotes: string[] = [];
  screens.forEach((s) => {
    (s.blocks ?? []).forEach((block) => {
      if (block.type !== REVIEW_BLOCK_TYPE) return;
      const items = (block.testimonialItems ?? []) as ReviewItem[];
      items.forEach((item) => {
        if (item?.text == null) return;
        // Текст отзыва — rich-поле (WEB-809): в карусель обязан ехать простой
        // текст, иначе на экране окажется разметка.
        const text = richFieldToPlainText(item.text).trim();
        if (text === '' || quotes.includes(text)) return;
        quotes.push(text);
      });
    });
  });
  return quotes.slice(0, limit);
}
